import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios, { FILE_URL } from '../api/axios';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [rememberMe, setRememberMe] = useState(false);
    const { settings, darkMode } = useTheme();
    const { t, i18n } = useTranslation();

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    useEffect(() => {
        const savedEmail = localStorage.getItem('rememberedEmail');
        if (savedEmail) {
            setEmail(savedEmail);
            setRememberMe(true);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Backend'e istek at
            const response = await axios.post('/auth/login', { email, password });
            
            // Gelen Token'ı ve Kullanıcı bilgisini tarayıcı hafızasına (LocalStorage) kaydet
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem('token', response.data.token);
            storage.setItem('user', JSON.stringify(response.data.user));

            if (rememberMe) {
                localStorage.setItem('rememberedEmail', email);
            } else {
                localStorage.removeItem('rememberedEmail');
            }
            
            toast.success('Giriş başarılı! Yönlendiriliyorsunuz...');
            
            // Başarılıysa Dashboard sayfasına git
            navigate('/dashboard');
            
        } catch (error) {
            // Hata varsa ekrana bas
            toast.error(error.response?.data?.message || 'Giriş başarısız.');
        }
    };

    const logoSrc = darkMode && settings.logo_dark 
        ? `${FILE_URL}/${settings.logo_dark}`
        : settings.logo_light 
            ? `${FILE_URL}/${settings.logo_light}`
            : null;

    return (
        <div className="container-fluid vh-100 p-0 overflow-hidden">
            <div className="row h-100 g-0">
                {/* Sol Taraf: Görsel */}
                <div className="col-lg-7 d-none d-lg-block position-relative bg-light">
                    {settings.login_image ? (
                        <img 
                            src={`${FILE_URL}/${settings.login_image}`} 
                            alt="Login Cover" 
                            className="w-100 h-100"
                            style={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-primary text-white">
                            <div className="text-center">
                                <h1 className="display-1 fw-bold">VegaMat</h1>
                                <p className="lead">B2B Bayi Yönetim Sistemi</p>
                            </div>
                        </div>
                    )}
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.8) 100%)' }}></div>
                    
                    <div className="position-absolute bottom-0 start-0 p-5 text-white" style={{ zIndex: 2 }}>
                        <h2 className="fw-bold mb-3" style={{ whiteSpace: 'pre-line' }}>{t('login.welcome_title')}</h2>
                        <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 border border-white border-opacity-25 px-3 py-2 rounded-pill" style={{ backdropFilter: 'blur(10px)' }}>
                            <div className="rounded-circle bg-success" style={{ width: '8px', height: '8px', boxShadow: '0 0 8px #4ade80' }}></div>
                            <span className="small fw-medium text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>{t('login.secure_connection')}</span>
                        </div>
                    </div>
                </div>

                {/* Sağ Taraf: Giriş Formu */}
                <div className="col-lg-5 d-flex align-items-center justify-content-center bg-white position-relative">
                    
                    {/* Dil Seçeneği */}
                    <div className="position-absolute top-0 end-0 p-4">
                        <div className="btn-group btn-group-sm shadow-sm">
                            <button 
                                className={`btn ${i18n.language === 'tr' ? 'btn-primary' : 'btn-outline-primary'}`} 
                                onClick={() => changeLanguage('tr')}
                            >
                                TR
                            </button>
                            <button 
                                className={`btn ${i18n.language === 'fr' ? 'btn-primary' : 'btn-outline-primary'}`} 
                                onClick={() => changeLanguage('fr')}
                            >
                                FR
                            </button>
                        </div>
                    </div>

                    <div className="w-100 p-5" style={{ maxWidth: '500px' }}>
                        <div className="text-center mb-5">
                            {logoSrc && (
                                <img 
                                    src={logoSrc} 
                                    alt="Logo" 
                                    className="mb-4" 
                                    style={{ maxHeight: '60px' }} 
                                />
                            )}
                            <h2 className="fw-bold text-dark">{settings.site_title || t('login.welcome_subtitle')}</h2>
                            <p className="text-muted">{t('login.login_prompt')}</p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="form-label fw-medium text-muted small text-uppercase">{t('login.email_label')}</label>
                                <input 
                                    type="email" 
                                    className="form-control form-control-lg bg-light border-0" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="ornek@vegamat.com"
                                    required 
                                />
                            </div>
                            <div className="mb-4">
                                <label className="form-label fw-medium text-muted small text-uppercase">{t('login.password_label')}</label>
                                <input 
                                    type="password" 
                                    className="form-control form-control-lg bg-light border-0" 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="******"
                                    required 
                                />
                            </div>

                            <div className="mb-4 d-flex justify-content-between align-items-center">
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                                    <label className="form-check-label text-muted small" htmlFor="rememberMe">
                                        {t('login.remember_me')}
                                    </label>
                                </div>
                            </div>

                            <button type="submit" className="btn btn-primary btn-lg w-100 mb-4">{t('login.submit_btn')}</button>
                            
                            <div className="text-center mb-4">
                                <span className="text-muted">{t('login.not_dealer')} </span>
                                <Link to="/register" className="text-decoration-none fw-bold">{t('login.apply_now')}</Link>
                            </div>
                        </form>

                        <div className="text-center mt-5 text-muted small opacity-50">
                            by AMC Fikir Sanat &copy; 2024 v2.6
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
